// utility/image-url.ts
/**
 * Transforms image srcset strings to handle URLs with commas and clean up whitespace
 * Normalizes both single-line and multi-line srcsets into a clean single-line format
 * @param srcset The image srcset string to transform
 * @returns Cleaned and encoded srcset string
 * @example
 * // Multi-line input:
 * `
 *   /img1.jpg 360w,
 *   /img2.jpg 720w,
 *   /img3.jpg 1080w
 * `
 * // Single-line input:
 * "/img1.jpg 360w, /img2.jpg 720w, /img3.jpg 1080w"
 * // Both produce normalized output:
 * "/img1.jpg 360w,/img2.jpg 720w,/img3.jpg 1080w"
 */
export function transformImageSrcSet(srcset: string): string {
  return srcset
    .trim() // Remove leading/trailing whitespace and newlines
    .split('\n') // Split into lines
    .map((line) => line.trim()) // Trim each line
    .join('') // Rejoin into single line
    .replace(/\s+(?=\d+w)/g, ' ') // Single space before width descriptors
    .replace(/\s+(?!\d+w)/g, '') // Remove spaces not before width descriptors
    .replace(/,\s*/g, ',') // Remove spaces after commas
    .replace(/,(?=\w)/g, '%2C') // Encode commas before word chars
}

/**
 * Transforms a single image URL to handle commas
 * @param url The image URL to transform
 * @returns Cleaned and encoded URL string
 */
export function transformImageUrl(url: string): string {
  return url.replace(/,(?=\w)/g, '%2C')
}
